import { useState } from 'react'

import * as Sentry from '@sentry/nextjs'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import Script from 'next/script'
import styled, { css } from 'styled-components'

import { useBreakpoint } from '../../hooks/useBreakpoint'
import TrialBanner from '../../modules/builder/TrialBanner'
import { LiveChat } from '../util-components/live-chat/LiveChat'

import { CorePageFooter } from './CorePageFooter'
import {
  CorePageHeader,
  HEADER_HEIGHT,
  HEADER_HEIGHT_MOBILE,
  MOBILE_BREADCRUMBS_HEIGHT,
} from './CorePageHeader'
import { CorePageLoadingTitle, CorePageMetaTitle } from './CorePageMetaTitle'
import { CorePageNoAccess } from './CorePageNoAccess'
import { CorePageNoProjectPrompt } from './CorePageNoProjectPrompt'
import { CorePageProjectRequired } from './CorePageProjectRequired'
import { CorePageSideNav } from './CorePageSideNav'
import { CorePageSkeleton } from './CorePageSkeleton'

import ErrorBoundary from '@/components/util-components/ErrorBoundary'
import { TrackPage } from '@/components/util-components/TrackPage'
import { useAuth } from '@/context/authContext'
import { QueryParamsProvider } from '@/context/queryParamsContext'
import { useUser } from '@/context/userContext'
import SupportModeBanner from '@/modules/superAdmin/SupportModeBanner'

export const SIDE_CONTENT_PORTAL_ID = 'bp-side-content-portal'

const PWAInstallPrompt = dynamic(
  () => import('@/components/util-components/PWAInstallPrompt'),
  { ssr: false }
)

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.colour.neutral.background};
  display: grid;
  grid-template-columns: minmax(1px, 1fr);
  grid-template-rows: ${HEADER_HEIGHT_MOBILE}px ${MOBILE_BREADCRUMBS_HEIGHT}px 1fr auto;
  grid-template-areas:
    'header'
    'breadcrumbs'
    'content'
    'footer';
  min-height: 100vh;

  ${({ theme: { media } }) => media.lg`
    grid-template-columns: auto minmax(1px, 1fr); // minmax required for breadcrumb overflow
    grid-template-rows: ${HEADER_HEIGHT}px 1fr auto;
    grid-template-areas:
      'side header'
      'side content'
      'side footer';
    `}
`

const SplitLayout = styled.div`
  grid-area: content;

  ${({ theme: { media } }) => media.lg`
    display: flex;
    flex-direction: row;
  `}
`

const Main = styled.main<Props>`
  width: 100%;
  flex-grow: 1;
  position: relative;
  overflow: auto; // important for x-axis overflow on mobile

  ${({ easyPadding }) =>
    easyPadding &&
    css`
      padding: 1rem;
    `}

  ${({ theme, whiteBg }) =>
    whiteBg &&
    css`
      background-color: ${theme.colour.neutral.white};
    `}

  ${({ fullHeightContent }) =>
    fullHeightContent &&
    css`
      display: flex;
      flex-direction: column;
    `}
`

const SideContent = styled.div<{ whiteBg?: boolean }>`
  &:not(:empty) {
    background-color: ${({ theme, whiteBg }) =>
      whiteBg ? theme.colour.neutral.white : theme.colour.neutral.surface};

    ${({ theme: { media } }) => media.xl`
      flex-shrink: 0; // prevent main content from making this shrink
      position: sticky;
      top: 0;
      width: 440px;
      border-left: 1px solid ${({ theme }) => theme.colour.neutral.border};
      max-height: 100dvh; // account for floating bars on mobile
      overflow: auto;
    `}
  }
`

export const LoadingSkeleton = () => (
  <Wrapper>
    <CorePageLoadingTitle />
    <CorePageSkeleton />
  </Wrapper>
)

type Props = {
  easyPadding?: boolean
  backUrl?: string
  whiteBg?: boolean
  sideContentWhiteBg?: boolean
  forceDrawer?: boolean
  fullHeightContent?: boolean
  children?: React.ReactNode
}

export const CorePage: React.FC<Props> = ({
  easyPadding,
  backUrl,
  whiteBg,
  sideContentWhiteBg,
  forceDrawer,
  fullHeightContent,
  children,
}) => {
  const isSmall = useBreakpoint('lg')

  const { userLoading } = useUser()
  const { isReady } = useRouter()

  const { isLoaded, isSignedIn, authenticationError } = useAuth()

  const [drawerOpen, setDrawerOpen] = useState(false)

  const openDrawer = () => setDrawerOpen(true)
  const closeDrawer = () => setDrawerOpen(false)

  /**
   * The `isReady` check is necessary because the `useRouter` hook is empty on the first render before hydration.
   * This ensures any children components that rely on the router are not rendered until the router is ready.
   */
  if (!isLoaded || !isReady || userLoading) {
    return <LoadingSkeleton />
  }

  if (isLoaded && isReady) {
    if (!isSignedIn) {
      Sentry.captureMessage('Tried to render CorePage without authentication')

      if (process.env.NODE_ENV === 'development') {
        console.error(
          'This page requires auth! Make sure you add this route to the middleware.ts file'
        )
      } else {
        console.error('This page requires auth!')
      }
      authenticationError()
      return null
    }
  }

  return (
    <>
      <CorePageMetaTitle />
      {!isSmall && <TrialBanner />}
      <Wrapper>
        <CorePageSideNav
          drawerOpen={drawerOpen}
          forceDrawer={forceDrawer}
          closeDrawer={closeDrawer}
        />
        <CorePageHeader openDrawer={openDrawer} backUrl={backUrl} />
        <ErrorBoundary>
          <SplitLayout>
            <Main
              easyPadding={easyPadding}
              whiteBg={whiteBg}
              fullHeightContent={fullHeightContent}
            >
              <CorePageNoAccess>
                <CorePageNoProjectPrompt>
                  <CorePageProjectRequired>
                    <QueryParamsProvider>{children}</QueryParamsProvider>
                  </CorePageProjectRequired>
                </CorePageNoProjectPrompt>
              </CorePageNoAccess>
            </Main>
            <SideContent
              id={SIDE_CONTENT_PORTAL_ID}
              whiteBg={sideContentWhiteBg}
            />
          </SplitLayout>
        </ErrorBoundary>
        <CorePageFooter />
      </Wrapper>
      <TrackPage />

      <LiveChat />
      <PWAInstallPrompt />
      <SupportModeBanner />
      <Script type="text/javascript" id="user-guiding">
        {`(function(g,u,i,d,e,s){g[e]=g[e]||[];var f=u.getElementsByTagName(i)[0];var k=u.createElement(i);k.async=true;k.src='https://static.userguiding.com/media/user-guiding-'+s+'-embedded.js';f.parentNode.insertBefore(k,f);if(g[d])return;var ug=g[d]={q:[]};ug.c=function(n){return function(){ug.q.push([n,arguments])};};var m=['previewGuide','finishPreview','track','identify', 'hideChecklist','launchChecklist'];for(var j=0;j<m.length;j+=1){ug[m[j]]=ug.c(m[j]);}})(window,document,'script','userGuiding','userGuidingLayer','Z3Y77160CL9ID');`}
      </Script>
    </>
  )
}
